import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const TourContext = createContext(null);

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Comprehensive tour chapters covering the entire KEY-MADE workflow
export const TOUR_CHAPTERS = [
  {
    id: 'welcome',
    title: 'Welcome to KEY-MADE',
    description: 'Your comprehensive project management platform for Architectural and Interior Designers',
    icon: '🏠',
    route: '/dashboard',
    duration: '3 min',
    steps: [
      {
        target: 'body',
        content: 'Welcome to KEY-MADE! This is your My Desk - the central hub where all your projects, tasks, and activities come together.',
        title: 'Your Dashboard',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="nav-sidebar"]',
        content: 'The sidebar navigation gives you quick access to all major areas of KEY-MADE. Let\'s explore each section.',
        title: 'Navigation',
        placement: 'right',
      },
      {
        target: '[data-testid="quick-actions"]',
        content: 'Quick Actions let you jump straight into common tasks like creating a new project, adding clips, or generating estimates.',
        title: 'Quick Actions',
        placement: 'top',
      },
    ]
  },
  {
    id: 'project-setup',
    title: 'Project Setup',
    description: 'Learn how to create and configure new projects',
    icon: '📋',
    route: '/dashboard',
    duration: '5 min',
    steps: [
      {
        target: '[data-testid="create-project-btn"]',
        content: 'Start by creating a new project. Click here to open the project creation dialog.',
        title: 'Create Your First Project',
        placement: 'bottom',
      },
      {
        target: '[data-testid="project-card"]',
        content: 'Each project appears as a card showing key information: client name, project type, status, and quick actions.',
        title: 'Project Cards',
        placement: 'right',
      },
      {
        target: '[data-testid="project-status"]',
        content: 'Project status helps you track where each project stands in your workflow - from Initial Consultation to Project Complete.',
        title: 'Project Status',
        placement: 'top',
      },
    ]
  },
  {
    id: 'project-hub',
    title: 'Project Hub',
    description: 'Master the central workspace for each project',
    icon: '🎯',
    route: null, // Dynamic - requires project ID
    duration: '8 min',
    requiresProject: true,
    steps: [
      {
        target: '[data-testid="project-hub-header"]',
        content: 'The Project Hub is your command center for each project. Everything you need is organized into tabs.',
        title: 'Project Command Center',
        placement: 'bottom',
        disableBeacon: true,
      },
      {
        target: '[data-testid="project-tabs"]',
        content: 'Navigate between different aspects of your project: Overview, Plans, Mood Boards, Clips, Estimates, Portals, and more.',
        title: 'Project Tabs',
        placement: 'bottom',
      },
      {
        target: '[data-testid="project-journey"]',
        content: 'The Journey section tracks your project through customizable phases. Update progress as you hit milestones.',
        title: 'Project Journey',
        placement: 'right',
      },
      {
        target: '[data-testid="project-action-items"]',
        content: 'Action Items keep your team on track. Create tasks, assign due dates, and mark items complete.',
        title: 'Action Items',
        placement: 'left',
      },
    ]
  },
  {
    id: 'design-tools',
    title: 'Design Tools',
    description: 'Floor Plans, Room Takeoff, and Mood Boards',
    icon: '🖼️',
    route: null,
    duration: '10 min',
    requiresProject: true,
    steps: [
      {
        target: '[data-testid="floor-plans-tab"]',
        content: 'Upload your floor plans here. You can annotate them, tag rooms, and link them to your clip selections.',
        title: 'Floor Plans',
        placement: 'bottom',
      },
      {
        target: '[data-testid="nav-plans-takeoff"]',
        content: 'The Takeoff lets you define rooms on your floor plans. These rooms become the foundation for organizing your clips and estimates.',
        title: 'Takeoff',
        placement: 'right',
      },
      {
        target: '[data-testid="mood-boards-tab"]',
        content: 'Create beautiful mood boards to present design concepts to clients. Drag and drop images, add annotations, and share via the Drawing Room.',
        title: 'Mood Boards',
        placement: 'bottom',
      },
    ]
  },
  {
    id: 'clipper-tool',
    title: 'The Clipper',
    description: 'Save products from anywhere on the web',
    icon: '✂️',
    route: '/clips',
    duration: '6 min',
    steps: [
      {
        target: '[data-testid="clips-library"]',
        content: 'Your Clips Library stores all the products you\'ve saved from across the web. Organize by project, room, or category.',
        title: 'Clips Library',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="clip-card"]',
        content: 'Each clip contains product details: image, price, vendor, specifications, and your notes. Click to view full details.',
        title: 'Clip Cards',
        placement: 'right',
      },
      {
        target: '[data-testid="clips-filter"]',
        content: 'Filter and search your clips by project, room, category, vendor, or approval status.',
        title: 'Filter & Search',
        placement: 'bottom',
      },
    ]
  },
  {
    id: 'browser-extension',
    title: 'Chrome Extension',
    description: 'How to use the KEY-MADE Web Clipper',
    icon: '🔌',
    route: '/clips',
    duration: '4 min',
    isVideoChapter: true,
    videoPlaceholder: 'chrome-extension-tutorial',
    steps: [
      {
        target: '[data-testid="clips-library"]',
        content: 'The KEY-MADE Web Clipper is a Chrome extension that lets you save products while browsing vendor websites.\n\n📹 Watch the video tutorial to learn how to:\n• Install the extension\n• Save products with one click\n• Add pricing and specifications\n• Assign clips to projects and rooms',
        title: 'Web Clipper Extension',
        placement: 'center',
        disableBeacon: true,
      },
    ]
  },
  {
    id: 'estimates',
    title: 'Creating Estimates',
    description: 'Build detailed project estimates from your clips',
    icon: '💰',
    route: '/financials',
    duration: '8 min',
    steps: [
      {
        target: '[data-testid="financials-nav"]',
        content: 'The Financials section is where you manage all monetary aspects of your projects: Estimates, Invoices, RFPs, Purchase Orders, and Change Orders.',
        title: 'Financial Management',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="estimates-section"]',
        content: 'Estimates pull from your approved clips. You can auto-fill from Room Takeoff and the Clipper to save time.',
        title: 'Estimates',
        placement: 'right',
      },
      {
        target: '[data-testid="estimate-autofill"]',
        content: 'Auto-Fill automatically populates your estimate with rooms from TakeOff and items from the Clipper. No manual data entry needed!',
        title: 'Auto-Fill Feature',
        placement: 'bottom',
      },
    ]
  },
  {
    id: 'rfp-workflow',
    title: 'RFP Workflow',
    description: 'Request quotes from vendors',
    icon: '📨',
    route: '/financials/rfps',
    duration: '5 min',
    steps: [
      {
        target: '[data-testid="rfps-page"]',
        content: 'Request for Proposals (RFPs) help you gather quotes from vendors for your project items.',
        title: 'RFPs Overview',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="create-rfp-btn"]',
        content: 'Create an RFP by selecting items from your clips. Include quantity, specifications, and delivery requirements.',
        title: 'Create RFP',
        placement: 'bottom',
      },
      {
        target: '[data-testid="rfp-status"]',
        content: 'Track RFP status from Draft to Sent to Received. Compare vendor responses side-by-side.',
        title: 'Track Responses',
        placement: 'right',
      },
    ]
  },
  {
    id: 'purchase-orders',
    title: 'Purchase Orders',
    description: 'Convert quotes to orders',
    icon: '🛒',
    route: '/financials',
    duration: '5 min',
    steps: [
      {
        target: '[data-testid="purchase-orders-section"]',
        content: 'Once you accept a vendor quote, convert it to a Purchase Order. This creates a formal order with all specifications.',
        title: 'Purchase Orders',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="po-tracking"]',
        content: 'Track each PO through its lifecycle: Created, Sent, Acknowledged, Shipped, and Received.',
        title: 'Order Tracking',
        placement: 'right',
      },
    ]
  },
  {
    id: 'invoicing',
    title: 'Invoicing',
    description: 'Generate and manage client invoices',
    icon: '📄',
    route: '/financials/invoices',
    duration: '5 min',
    steps: [
      {
        target: '[data-testid="invoices-page"]',
        content: 'Generate professional invoices from your estimates. Include your branding, payment terms, and itemized details.',
        title: 'Invoices',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="invoice-status"]',
        content: 'Track payment status: Draft, Sent, Viewed, Paid, or Overdue. Send reminders automatically.',
        title: 'Payment Tracking',
        placement: 'right',
      },
      {
        target: '[data-testid="accounting-integration"]',
        content: 'Connect to Xero or QuickBooks to sync invoices with your accounting software.',
        title: 'Accounting Integration',
        placement: 'bottom',
      },
    ]
  },
  {
    id: 'change-orders',
    title: 'Change Orders',
    description: 'Manage scope changes professionally',
    icon: '🔄',
    route: '/financials/change-orders',
    duration: '4 min',
    steps: [
      {
        target: '[data-testid="change-orders-page"]',
        content: 'Change Orders document modifications to the original project scope. Always get client approval before proceeding.',
        title: 'Change Orders',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="co-approval"]',
        content: 'Track approval status and link change orders to affected estimates and invoices.',
        title: 'Approval Workflow',
        placement: 'right',
      },
    ]
  },
  {
    id: 'client-portals',
    title: 'Client Portals',
    description: 'Share designs with clients via Drawing Room',
    icon: '🚪',
    route: null,
    duration: '7 min',
    requiresProject: true,
    steps: [
      {
        target: '[data-testid="portals-tab"]',
        content: 'KEY-MADE provides branded portals for client collaboration. The Drawing Room is where clients review and approve your designs.',
        title: 'Client Portals',
        placement: 'bottom',
      },
      {
        target: '[data-testid="drawing-room-link"]',
        content: 'Generate a secure link to share with your client. They can view mood boards, approve selections, and communicate with you.',
        title: 'Drawing Room Access',
        placement: 'right',
      },
      {
        target: '[data-testid="client-approvals"]',
        content: 'When clients approve items in the Drawing Room, they automatically receive a SpecNum and move to your procurement queue.',
        title: 'Client Approvals',
        placement: 'left',
      },
    ]
  },
  {
    id: 'contractor-management',
    title: 'Contractor Management',
    description: 'Manage contractors and their portals',
    icon: '👷',
    route: '/contractors',
    duration: '6 min',
    steps: [
      {
        target: '[data-testid="contractors-page"]',
        content: 'Manage your network of contractors: General Contractors, Sub-contractors, vendors, and consultants.',
        title: 'Contractor Directory',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="contractor-hierarchy"]',
        content: 'Organize contractors with parent-child relationships. Link sub-contractors to their General Contractor.',
        title: 'Contractor Hierarchy',
        placement: 'right',
      },
      {
        target: '[data-testid="site-office-portal"]',
        content: 'The Site Office portal gives contractors access to project documents, RFIs, and submittals.',
        title: 'Site Office Portal',
        placement: 'bottom',
      },
    ]
  },
  {
    id: 'documents',
    title: 'Document Library',
    description: 'Templates, contracts, and file management',
    icon: '📁',
    route: '/documents',
    duration: '5 min',
    steps: [
      {
        target: '[data-testid="document-library"]',
        content: 'Store all your project documents in one place: contracts, agreements, specifications, and more.',
        title: 'Document Library',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-testid="document-templates"]',
        content: 'Create reusable templates for contracts, proposals, and other common documents.',
        title: 'Templates',
        placement: 'right',
      },
      {
        target: '[data-testid="generate-document"]',
        content: 'Generate documents directly from estimates. Choose from templates and customize as needed.',
        title: 'Document Generation',
        placement: 'bottom',
      },
    ]
  },
  {
    id: 'blueprints-help',
    title: 'Getting Help',
    description: 'Using Blueprints and the help system',
    icon: '❓',
    route: '/blueprints',
    duration: '3 min',
    steps: [
      {
        target: '[data-testid="blueprint-library"]',
        content: 'The Blueprint Library contains detailed guides for every feature in KEY-MADE. Search or browse by category.',
        title: 'Blueprint Library',
        placement: 'center',
        disableBeacon: true,
      },
      {
        target: '[data-blueprint-indicator]',
        content: 'Look for these help indicators throughout the app. Click them to get contextual help for that specific feature.',
        title: 'Help Indicators',
        placement: 'right',
      },
      {
        target: '[data-testid="terms-glossary"]',
        content: 'The Terms Glossary explains industry terminology and how each term applies to KEY-MADE features.',
        title: 'Terms Glossary',
        placement: 'bottom',
      },
    ]
  },
];

// Tour settings with progress persistence
const TOUR_STORAGE_KEY = 'keymade-tour-progress';

// Load saved progress from localStorage
const loadSavedProgress = () => {
  try {
    const saved = localStorage.getItem(TOUR_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading tour progress:', e);
  }
  return { completedChapters: [], hasSeenTour: false };
};

export const TourProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize state from localStorage using lazy initialization
  const savedProgress = loadSavedProgress();
  
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedChapters, setCompletedChapters] = useState(savedProgress.completedChapters);
  const [tourMode, setTourMode] = useState('full'); // 'full' | 'chapter' | 'quick'
  const [isPaused, setIsPaused] = useState(false);
  const [hasSeenTour, setHasSeenTour] = useState(savedProgress.hasSeenTour);
  const [selectedProject, setSelectedProject] = useState(null);

  // Save progress to localStorage
  useEffect(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify({
      completedChapters,
      hasSeenTour,
    }));
  }, [completedChapters, hasSeenTour]);

  // Get current chapter
  const currentChapter = TOUR_CHAPTERS[currentChapterIndex];
  
  // Get current steps for the active chapter
  const currentSteps = currentChapter?.steps || [];
  
  // Calculate overall progress
  const totalSteps = TOUR_CHAPTERS.reduce((sum, ch) => sum + ch.steps.length, 0);
  const completedSteps = TOUR_CHAPTERS.slice(0, currentChapterIndex).reduce((sum, ch) => sum + ch.steps.length, 0) + currentStepIndex;
  const overallProgress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // End tour - defined early as it's used by other callbacks
  const endTour = useCallback((opts = {}) => {
    setIsTourActive(false);
    setIsPaused(false);
    if (opts.completed) {
      // Light celebration when the user finishes the Welcome Tour
      try {
        toast.success("You're all set!", {
          description: "Welcome Tour complete — explore at your own pace.",
          duration: 4000,
        });
      } catch {
        /* toast unavailable in tests/SSR — no-op */
      }
    }
  }, []);

  // Pause/Resume tour
  const togglePause = useCallback(() => {
    setIsPaused(prev => !prev);
  }, []);

  // Reset tour progress
  const resetTourProgress = useCallback(() => {
    setCompletedChapters([]);
    setHasSeenTour(false);
    localStorage.removeItem(TOUR_STORAGE_KEY);
  }, []);

  // Start the full tour
  const startFullTour = useCallback((projectId = null) => {
    setSelectedProject(projectId);
    setCurrentChapterIndex(0);
    setCurrentStepIndex(0);
    setTourMode('full');
    setIsTourActive(true);
    setIsPaused(false);
    setHasSeenTour(true);
    
    // Navigate to the first chapter's route
    const firstChapter = TOUR_CHAPTERS[0];
    if (firstChapter.route) {
      navigate(firstChapter.route);
    }
  }, [navigate]);

  // Start a specific chapter
  const startChapter = useCallback((chapterId, projectId = null) => {
    const chapterIndex = TOUR_CHAPTERS.findIndex(ch => ch.id === chapterId);
    if (chapterIndex === -1) return;
    
    setSelectedProject(projectId);
    setCurrentChapterIndex(chapterIndex);
    setCurrentStepIndex(0);
    setTourMode('chapter');
    setIsTourActive(true);
    setIsPaused(false);
    
    const chapter = TOUR_CHAPTERS[chapterIndex];
    if (chapter.route) {
      navigate(chapter.route);
    }
  }, [navigate]);

  // Move to next step
  const nextStep = useCallback(() => {
    if (currentStepIndex < currentSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      // Move to next chapter
      if (tourMode === 'full' && currentChapterIndex < TOUR_CHAPTERS.length - 1) {
        // Mark current chapter as completed
        if (!completedChapters.includes(currentChapter.id)) {
          setCompletedChapters(prev => [...prev, currentChapter.id]);
        }
        
        const nextChapterIndex = currentChapterIndex + 1;
        const nextChapter = TOUR_CHAPTERS[nextChapterIndex];
        
        // Skip chapters that require a project if none selected
        if (nextChapter.requiresProject && !selectedProject) {
          // Find next chapter that doesn't require project
          let skipIndex = nextChapterIndex;
          while (skipIndex < TOUR_CHAPTERS.length && TOUR_CHAPTERS[skipIndex].requiresProject && !selectedProject) {
            skipIndex++;
          }
          if (skipIndex < TOUR_CHAPTERS.length) {
            setCurrentChapterIndex(skipIndex);
            setCurrentStepIndex(0);
            if (TOUR_CHAPTERS[skipIndex].route) {
              navigate(TOUR_CHAPTERS[skipIndex].route);
            }
          } else {
            endTour({ completed: true });
          }
        } else {
          setCurrentChapterIndex(nextChapterIndex);
          setCurrentStepIndex(0);
          if (nextChapter.route) {
            navigate(nextChapter.route);
          } else if (nextChapter.requiresProject && selectedProject) {
            navigate(`/projects/${selectedProject}/hub`);
          }
        }
      } else {
        // End of tour or single chapter
        if (!completedChapters.includes(currentChapter.id)) {
          setCompletedChapters(prev => [...prev, currentChapter.id]);
        }
        // Only celebrate when the user actually completed the full tour
        // (not when they exited a single chapter early).
        endTour({ completed: tourMode === 'full' });
      }
    }
  }, [currentStepIndex, currentSteps.length, currentChapterIndex, currentChapter, tourMode, completedChapters, selectedProject, navigate, endTour]);

  // Move to previous step
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    } else if (currentChapterIndex > 0) {
      const prevChapterIndex = currentChapterIndex - 1;
      const prevChapter = TOUR_CHAPTERS[prevChapterIndex];
      setCurrentChapterIndex(prevChapterIndex);
      setCurrentStepIndex(prevChapter.steps.length - 1);
      if (prevChapter.route) {
        navigate(prevChapter.route);
      }
    }
  }, [currentStepIndex, currentChapterIndex, navigate]);

  // Skip to a specific chapter
  const skipToChapter = useCallback((chapterIndex) => {
    if (chapterIndex >= 0 && chapterIndex < TOUR_CHAPTERS.length) {
      setCurrentChapterIndex(chapterIndex);
      setCurrentStepIndex(0);
      const chapter = TOUR_CHAPTERS[chapterIndex];
      if (chapter.route) {
        navigate(chapter.route);
      }
    }
  }, [navigate]);

  const value = {
    // State
    isTourActive,
    isPaused,
    currentChapter,
    currentChapterIndex,
    currentStepIndex,
    currentSteps,
    completedChapters,
    tourMode,
    hasSeenTour,
    selectedProject,
    overallProgress,
    totalChapters: TOUR_CHAPTERS.length,
    
    // Actions
    startFullTour,
    startChapter,
    nextStep,
    prevStep,
    skipToChapter,
    togglePause,
    endTour,
    resetTourProgress,
    setSelectedProject,
    
    // Data
    chapters: TOUR_CHAPTERS,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export default TourContext;
